import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'sonner';
import { Check, ChevronRight, CreditCard, ExternalLink, Loader2, Monitor, Send } from 'lucide-react';
import { db, cloudFunctions, logAnalyticsEvent } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { GlassCard } from '@/components/ui/GlassCard';
import { cn } from '@/lib/utils';
import { PayoutCountryPicker, suggestPayoutCountry, useConnectCountries } from '@/components/payouts/PayoutCountryPicker';
import { NICHES } from '@shared/types';

const steps = ['Company Info', 'Select Niches', 'Payouts Setup'];

// Mobile-only soft nudge shown on the post-checkout onboarding page. Romulus
// is designed to be run from a desktop, so once a user has successfully paid
// and created their account on their phone we encourage them to jump to their
// laptop to finish setup - without blocking them from continuing on mobile.
function MobileDesktopHandoff() {
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    logAnalyticsEvent('mobile_desktop_handoff_shown', { page: 'onboarding' });
  }, []);

  if (dismissed) return null;

  const loginUrl = `${window.location.origin}/login`;

  const handleSendLink = async () => {
    setSending(true);
    logAnalyticsEvent('mobile_desktop_handoff_send_clicked', { page: 'onboarding' });
    const shareData = {
      title: 'Romulus: finish setup on your laptop',
      text: `Open this on your laptop and sign in to finish setting up Romulus: ${loginUrl}`,
      url: loginUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        logAnalyticsEvent('mobile_desktop_handoff_share_completed', { page: 'onboarding' });
      } else {
        await navigator.clipboard.writeText(loginUrl);
        toast.success('Link copied. Open it on your laptop');
        logAnalyticsEvent('mobile_desktop_handoff_link_copied', { page: 'onboarding' });
      }
    } catch {
      // user cancelled share sheet - no-op
    } finally {
      setSending(false);
    }
  };

  const handleDismiss = () => {
    logAnalyticsEvent('mobile_desktop_handoff_dismissed', { page: 'onboarding' });
    setDismissed(true);
  };

  return (
    <div
      className="md:hidden mb-6 rounded-xl border border-primary-500/40 bg-primary-500/[0.08] p-5"
      style={{ boxShadow: '0 0 40px rgba(91,58,58,0.25)' }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Monitor className="w-4 h-4 text-primary-300" />
        <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-primary-300">
          Pro tip
        </p>
      </div>
      <p className="text-white font-bold text-[17px] leading-tight mb-1.5">
        Finish setup on your laptop.
      </p>
      <p className="text-gray-400 text-[13px] leading-relaxed mb-4">
        Romulus has more room to breathe on desktop, and you'll be running campaigns from there anyway. Your account is already live, so just sign in and pick up where you left off.
      </p>
      <div className="flex flex-col gap-2">
        <Button onClick={handleSendLink} loading={sending} className="w-full h-11 text-[14px]">
          <span className="flex items-center justify-center gap-2">
            <Send className="w-4 h-4" />
            Email me the link
          </span>
        </Button>
        <button
          onClick={handleDismiss}
          className="w-full h-10 text-[13px] text-gray-500 hover:text-white transition-colors"
        >
          Continue on mobile
        </button>
      </div>
    </div>
  );
}

export function OnboardingPage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [companyName, setCompanyName] = useState('');
  const [selectedNiches, setSelectedNiches] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [payoutCountry, setPayoutCountry] = useState('');
  const [canChangePayoutCountry, setCanChangePayoutCountry] = useState(true);
  const { data: connectCountries, isLoading: loadingConnectCountries } = useConnectCountries();
  const [connectStatus, setConnectStatus] = useState<'pending' | 'in_progress' | 'complete'>('pending');
  const { user, userData } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // If the user was deep-linked here from the Send-to-business gate, we
  // return them to the page they came from after finishing setup instead
  // of dumping them on the generic dashboard.
  const returnTo = searchParams.get('return');

  // Hydrate the local connectStatus from Firestore so users who already
  // completed Stripe Connect don't land on "Connect with Stripe" again on
  // a refresh. Also opportunistically re-sync with Stripe in case the
  // account.updated webhook was missed.
  // Preselect the payout country from the seller's own evidence (existing
  // account, card country, browser locale) - never a fixed default.
  useEffect(() => {
    if (!connectCountries || payoutCountry) return;
    let cancelled = false;
    cloudFunctions.getConnectStatus({})
      .then((status) => {
        if (cancelled) return;
        setCanChangePayoutCountry(!status.hasAccount || !!status.canChangeCountry);
        const suggested = suggestPayoutCountry(connectCountries, status.billingCountry);
        setPayoutCountry(status.hasAccount && status.country && !(status.canChangeCountry && suggested) ? status.country : suggested);
      })
      .catch(() => {
        if (!cancelled) setPayoutCountry(suggestPayoutCountry(connectCountries));
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectCountries]);

  useEffect(() => {
    if (userData?.stripeConnectStatus === 'active') {
      setConnectStatus('complete');
    } else if (userData?.stripeConnectAccountId) {
      setConnectStatus('in_progress');
      cloudFunctions.getConnectStatus({})
        .then((status) => {
          if (status.canReceivePayments) setConnectStatus('complete');
        })
        .catch(() => { /* best-effort */ });
    }
  }, [userData?.stripeConnectStatus, userData?.stripeConnectAccountId]);

  const toggleNiche = (niche: string) => {
    if (selectedNiches.includes(niche)) {
      setSelectedNiches(selectedNiches.filter((n) => n !== niche));
    } else if (selectedNiches.length < 5) {
      setSelectedNiches([...selectedNiches, niche]);
    }
  };

  const handleStartConnect = async () => {
    setConnectLoading(true);
    logAnalyticsEvent('stripe_connect_started', { source: 'onboarding', country: payoutCountry });
    // Open window immediately to avoid popup blocker (must be in direct user gesture)
    const popup = window.open('about:blank', '_blank');
    try {
      // The country is required: the server creates the account in it, or moves
      // an account that isn't live yet into it. "Send to Business" is gated on
      // an active Connect account, so a wrong country here blocks selling.
      const result = await cloudFunctions.getConnectOnboardingLink({ country: payoutCountry });
      setConnectStatus('in_progress');
      if (popup) {
        popup.location.href = result.url;
      } else {
        window.open(result.url, '_blank');
      }
      toast.success('Stripe Connect opened in new tab. Complete the setup there.');
    } catch (error) {
      if (popup) {
        popup.close();
      }
      logAnalyticsEvent('stripe_connect_start_failed', { source: 'onboarding' });
      toast.error(error instanceof Error && error.message ? error.message : 'Failed to start Stripe Connect setup');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleCheckConnectStatus = async () => {
    setConnectLoading(true);
    try {
      const status = await cloudFunctions.getConnectStatus({});
      if (status.canReceivePayments) {
        setConnectStatus('complete');
        toast.success('Stripe Connect setup complete!');
      } else if (status.isOnboarded) {
        toast.info('Almost there! Please complete any remaining requirements.');
      } else {
        toast.info('Please complete the Stripe Connect setup in the new tab.');
      }
    } catch (error) {
      toast.error('Failed to check status');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 0 && !companyName.trim()) {
      toast.error('Please enter your company name');
      return;
    }
    if (currentStep === 1 && selectedNiches.length === 0) {
      toast.error('Please select at least one niche');
      return;
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    if (!user) return;
    setLoading(true);

    try {
      await updateDoc(doc(db, 'users', user.uid), {
        companyName,
        selectedNiches,
        onboardingCompleted: true,
        updatedAt: serverTimestamp(),
      });

      toast.success(returnTo ? 'Setup complete. You can send now.' : 'Welcome to Romulus!');
      navigate(returnTo || '/dashboard');
    } catch (error) {
      toast.error('Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <MobileDesktopHandoff />
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center overflow-hidden" style={{ boxShadow: '0 0 30px rgba(196,164,132,0.3)' }}>
            <img src="/wolfromulus.png" alt="Romulus" className="w-14 h-14 object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-white font-roman tracking-widest">ROMVLVS</h1>
        </div>

        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((step, index) => (
            <div key={step} className="flex items-center gap-2">
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all',
                  index < currentStep
                    ? 'bg-primary-500 text-white'
                    : index === currentStep
                    ? 'bg-primary-500/20 text-primary-400 border border-primary-500'
                    : 'bg-white/5 text-gray-500'
                )}
              >
                {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    'w-12 h-0.5 rounded',
                    index < currentStep ? 'bg-primary-500' : 'bg-white/10'
                  )}
                />
              )}
            </div>
          ))}
        </div>

        <GlassCard glow={currentStep === 3 ? 'purple' : currentStep === 2 ? 'purple' : 'blue'}>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-snow font-sans">{steps[currentStep]}</h2>
            <p className="text-gray-400 mt-2">
              {currentStep === 0 && "Let's get to know your business"}
              {currentStep === 1 && 'Select up to 5 niches to target'}
              {currentStep === 2 && 'Set up payouts for site sales'}
            </p>
          </div>

          {currentStep === 0 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  placeholder="Your Agency Name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto scrollbar-hide">
              {NICHES.map((niche) => (
                <button
                  key={niche}
                  type="button"
                  onClick={() => toggleNiche(niche)}
                  className={cn(
                    'p-3 rounded-lg text-sm font-medium text-left transition-all border',
                    selectedNiches.includes(niche)
                      ? 'bg-primary-500/20 border-primary-500 text-primary-300'
                      : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        'w-4 h-4 rounded border flex items-center justify-center',
                        selectedNiches.includes(niche)
                          ? 'bg-primary-500 border-primary-500'
                          : 'border-white/20'
                      )}
                    >
                      {selectedNiches.includes(niche) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    {niche}
                  </div>
                </button>
              ))}
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className={cn(
                  'w-20 h-20 rounded-2xl flex items-center justify-center',
                  connectStatus === 'complete'
                    ? 'bg-primary-500/20'
                    : 'bg-primary-500/20'
                )}>
                  {connectStatus === 'complete' ? (
                    <Check className="w-10 h-10 text-primary-400" />
                  ) : (
                    <CreditCard className="w-10 h-10 text-primary-400" />
                  )}
                </div>
              </div>

              <div className="text-center space-y-2">
                {connectStatus === 'complete' ? (
                  <>
                    <h3 className="text-lg font-semibold text-primary-400">Payouts Ready!</h3>
                    <p className="text-gray-400 text-sm">
                      Your Stripe Connect account is set up. You'll receive payouts when customers buy sites.
                    </p>
                  </>
                ) : connectStatus === 'in_progress' ? (
                  <>
                    <h3 className="text-lg font-semibold text-yellow-400">Setup In Progress</h3>
                    <p className="text-gray-400 text-sm">
                      Complete the setup in the Stripe tab, then click "Check Status" below.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-white">Connect Stripe</h3>
                    <p className="text-gray-400 text-sm">
                      Set up Stripe Connect to receive payments when customers purchase sites you create.
                    </p>
                  </>
                )}
              </div>

              {/* Country. Asked before the account exists, and still changeable
                  until the account is live (the server moves it). */}
              {connectStatus !== 'complete' && canChangePayoutCountry && (
                <div className="max-w-xs mx-auto w-full text-left">
                  <PayoutCountryPicker
                    value={payoutCountry}
                    onChange={setPayoutCountry}
                    countries={connectCountries}
                    loading={loadingConnectCountries}
                  />
                </div>
              )}

              <div className="flex flex-col gap-3">
                {connectStatus !== 'complete' && (
                  <Button
                    onClick={handleStartConnect}
                    loading={connectLoading}
                    disabled={canChangePayoutCountry && !payoutCountry}
                    className="w-full"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {connectStatus === 'in_progress' ? 'Reopen Stripe Setup' : 'Connect with Stripe'}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                )}

                {connectStatus === 'in_progress' && (
                  <Button
                    variant="outline"
                    onClick={handleCheckConnectStatus}
                    disabled={connectLoading}
                    className="w-full"
                  >
                    {connectLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Check Status
                  </Button>
                )}
              </div>

              <p className="text-xs text-gray-500 text-center">
                {connectStatus === 'complete'
                  ? 'You can manage your Stripe account from Settings anytime.'
                  : 'You can skip this step and set up payouts later from Settings.'}
              </p>
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            <Button
              variant="ghost"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
            >
              Back
            </Button>
            <Button onClick={handleNext} loading={loading}>
              {currentStep === steps.length - 1 ? 'Complete Setup' : 'Continue'}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
